import { createSlice } from '@reduxjs/toolkit';
import { translator } from 'translation/useTranslator';
import { replace } from 'utils/jsonPatch';
import { Category, Response, ResponseDataState } from '../../@types/response';
import axios from '../../utils/axios';
import { dispatch } from '../store';
import { rejectWithError } from '../dispatchError';

const initialState: ResponseDataState = {
  isLoading: false,
  response: null,
  isDeleteLoading: false
};

const slice = createSlice({
  name: 'response',
  initialState,
  reducers: {
    setResponse(state, action) {
      state.response = action.payload;
      state.isLoading = false;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { setResponse } = slice.actions;

interface AddCategoryToResponseProps {
  response: Response;
  category: Category | null;
}

export function patchResponseCategory({ response, category }: AddCategoryToResponseProps) {
  return async () => {
    const isAdd = category !== null;
    const defaultErrorMessage = isAdd
      ? translator?.couldNotAddCategoryToResponse() ?? 'Could not add category to response'
      : translator?.couldNotRemoveCategoryFromResponse() ??
        'Could not remove category from response';
    if (!response?.campaignMembershipGuid) {
      return rejectWithError({
        error: new Error(
          translator?.responseDoesNotHaveCampaignMembershipGuid() ??
            'Response does not have campaignMembershipGuid'
        ),
        defaultErrorMessage
      });
    }

    await axios
      .jsonPatch('/responses/' + response?.campaignMembershipGuid, [replace('/Category', category)])
      .catch((error: any) => {
        return rejectWithError({
          error: error,
          defaultErrorMessage
        });
      });

    dispatch(slice.actions.setResponse({ ...response }));
    return {
      defaultSuccessMessage: isAdd
        ? translator?.categoryAddedToResponse() ?? 'Category added to response'
        : translator?.categoryRemovedFromResponse() ?? 'Category removed from response'
    };
  };
}

export function editResponse(response: Response) {
  return async () => {
    await axios
      .put('/responses/' + response.campaignMembershipGuid, response)
      .catch((error: any) => {
        return rejectWithError({
          error: error,
          defaultErrorMessage: translator?.couldNotUpdateResponse() ?? 'Could not update response'
        });
      });

    dispatch(slice.actions.setResponse({ ...response }));
    return {
      defaultSuccessMessage: translator?.responseUpdated() ?? 'Response updated'
    };
  };
}

export function resendSurveys({ campaignId, ids }: { campaignId: number; ids: number[] }) {
  return async () => {
    let resendSurveysCounter = 0;
    let currentRespondentId = 0;
    try {
      for (const respondentId of ids ?? []) {
        currentRespondentId = respondentId;
        await axios.post(`/campaigns/${campaignId}/respondents/${respondentId}/resend`);
        resendSurveysCounter++;
      }
      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.surveysResent(`${resendSurveysCounter}`) ??
          `${resendSurveysCounter} survey${resendSurveysCounter > 1 ? 's' : ''} resent`
      });
    } catch (error) {
      return await rejectWithError({
        error: error,
        defaultErrorMessage: currentRespondentId
          ? translator?.couldNotResendSurveyWithId(`${currentRespondentId}`) ??
            `Could not resend survey with ID ${currentRespondentId}`
          : translator?.couldNotResendSurveys() ?? `Could not resend surveys`
      });
    }
  };
}
